import * as Logger from './logger.js'
import * as Utils from './utils.js';
import { HSCard } from './card.js';
import { UserSession } from './session.js';
import styles from './chart.css';
import template from './chart.html';
// echarts 
import * as echarts from 'echarts/core';
import { BarChart, LineChart, PieChart } from 'echarts/charts';
import { TitleComponent, TooltipComponent, GridComponent, DatasetComponent, TransformComponent, LegendComponent } from 'echarts/components';
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { CanvasRenderer } from 'echarts/renderers';
echarts.use([ BarChart, LineChart, PieChart, TitleComponent, TooltipComponent, GridComponent, DatasetComponent, TransformComponent, LabelLayout, UniversalTransition, CanvasRenderer, LegendComponent ]);

export class HSChart extends HSCard {
    exportableData;
    currentData = [];

    constructor() {
        super();
        //this.style.width = this.getAttribute('width') || '100%';
        //this.style.height = this.getAttribute('height') || '100%';
    }

    onLoad() {
        this.showLoading();
        this.loadTemplate(template);
        this.loadStyles(styles);
    }

    init(config) {
        super.init(config);
        if (config.h) { this.style.height = (config.h * 100) + 'px'; }
        requestAnimationFrame(() => {
            const parentNode = this.shadowRoot.querySelector('.chart-container');
            this.chart = echarts.init(parentNode);
            // read chart palette from CSS custom properties (falls back to ECharts defaults)
            const cs = getComputedStyle(this);
            const palette = [1,2,3,4,5,6].map(i => cs.getPropertyValue(`--chart-${i}`).trim()).filter(Boolean);
            const options = {
                ...(config.chartOptions || {}),
                useUTC: true
            };
            if (palette.length > 0) { options.color = palette; }
            // match chart title style to metric card titles
            options.title = Object.assign({
                textStyle: { color: '#113E57', fontSize: 16, fontWeight: 700 }
            }, options.title || {});
            // ensure both axes are present for cartesian charts to avoid ECharts init crash
            if (options.yAxis && !options.xAxis) {
                options.xAxis = config.xAxisType === 'category'
                    ? { type: 'category', data: [] }
                    : { type: 'time' };
            }
            this.chart.setOption(options);
            this.reloadData();
        });
    }

    updateData(update) {
        Logger.debug("chart got an update from SSE: ", update);
        const src = this.config.src;
        if (!src?.timeSeries || !this.config.xAxis) {
            // non-time-series charts don't support surgical updates
            return;
        }
        const xKey = this.config.xAxis;
        const xVal = update.obj?.[xKey];
        if (xVal === undefined || xVal === null) { return; }
        const xNum = parseInt(xVal);
        if (isNaN(xNum)) { return; }

        if (!update.obj) {
            // deletion
            this.currentData = this.currentData.filter(item => parseInt(item[xKey]) !== xNum);
        } else {
            const idx = this.currentData.findIndex(item => parseInt(item[xKey]) === xNum);
            if (idx >= 0) {
                this.currentData[idx] = { ...update.obj };
            } else {
                this.currentData.push({ ...update.obj });
                this.currentData.sort((a, b) => parseInt(a[xKey]) - parseInt(b[xKey]));
            }
        }

        // trim entries outside the current time window (mirrors getSourcePath time filters)
        if (src.applyTimeScopeFilters) {
            const timeScope = src.timeScope || UserSession.get('timeScope') || 'all';
            let cutoff = null;
            if (timeScope === 'day') {
                cutoff = Date.now() - (24 * 60 * 60 * 1000);
            } else if (timeScope === 'week') {
                cutoff = Date.now() - (7 * 24 * 60 * 60 * 1000);
            } else if (timeScope === 'quarter') {
                cutoff = Utils.getCurrentUTCQuarter();
            } else if (timeScope === 'month') {
                cutoff = Date.now() - (30 * 24 * 60 * 60 * 1000);
            }
            if (cutoff !== null) {
                this.currentData = this.currentData.filter(item => parseInt(item[xKey]) >= cutoff);
            }
        }

        this.renderChart([...this.currentData]);
    }

    async reloadData() {
        const json = await this.getSourceData(this.config.src);
        this.currentData = json;
        this.renderChart([...this.currentData]);
        this.hideLoading();
        this.redraw();
    }

    getFriendlyTimestamp(value, timeScope = 'hour', brief = false) {
        const d = new Date(value);
        if (brief) {
            if (timeScope === 'day') {
                return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            } else if (timeScope === 'quarter') {
                return `Q${Math.floor(d.getUTCMonth() / 3) + 1} ${d.getUTCFullYear()}`;
            } else if (timeScope === 'week' || timeScope === 'month') {
                return d.toLocaleDateString([], { month: '2-digit', day: '2-digit', timeZone: 'UTC' });
            } else {
                return d.toLocaleDateString([], { month: '2-digit', year: 'numeric', timeZone: 'UTC' });
            }
        } else {
            if (timeScope === 'day') {
                return d.toLocaleDateString([], { month: '2-digit', day: '2-digit', year: '2-digit', timeZone: 'UTC' }) + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            } else if (timeScope === 'quarter') {
                return `Q${Math.floor(d.getUTCMonth() / 3) + 1} ${d.getUTCFullYear()}`;
            } else if (timeScope === 'week' || timeScope === 'month') {
                return d.toLocaleDateString([], { month: '2-digit', day: '2-digit', year: '2-digit', timeZone: 'UTC' });
            } else {
                return d.toLocaleDateString([], { month: '2-digit', year: 'numeric', timeZone: 'UTC' });
            }
        }
    }

    formatDuration(ms) {
        const totalMinutes = Math.round(Number(ms) / 60000);
        if (totalMinutes < 1) return '< 1 min';
        const hours = Math.floor(totalMinutes / 60);
        const mins = totalMinutes % 60;
        if (hours === 0) return `${mins} min${mins !== 1 ? 's' : ''}`;
        if (mins === 0) return `${hours} hr${hours !== 1 ? 's' : ''}`;
        return `${hours} hr${hours !== 1 ? 's' : ''}, ${mins} min${mins !== 1 ? 's' : ''}`;
    }

    getHourLabel(hour) {
        const d = new Date(2020, 0, 1, hour, 0, 0);
        return d.toLocaleTimeString([], { hour: 'numeric' });
    }

    resolveXAxisLabels() {
        const labels = this.config.xAxisLabels;
        if (labels === 'hour_locale') {
            const map = {};
            for (let h = 0; h < 24; h++) { map[String(h)] = this.getHourLabel(h); }
            return map;
        }
        return labels;
    }

    renderChart(json) {
        let series = [];
        let legendData = [];
        let tooltipOptions = this.config.chartOptions?.tooltip || { trigger: 'axis' };
        let xAxisOptions = this.config.chartOptions?.xAxis || {};
        let isMultiLine = !!this.config.multiLineChart;
        let isTimeSeries = !!this.config.src?.timeSeries;
        let isCategoryAxis = this.config.xAxisType === 'category';
        let yAxisFields = Array.isArray(this.config.yAxis) ? this.config.yAxis : [this.config.yAxis];

        // dynamic series: auto-discover numeric keys from data as series
        if (this.config.dynamicSeries && json.length > 0) {
            const systemKeys = new Set(['id', 'providerid', 'useruuid', 'deviceuuid', 'orgid', 'field', 'stats_type', 'utc_timestamp_inms', 'timestamp', 'ts', this.config.xAxis]);
            const discovered = new Set();
            for (const row of json) {
                for (const [key, val] of Object.entries(row)) {
                    if (!systemKeys.has(key) && typeof val === 'number') {
                        discovered.add(key);
                    }
                }
            }
            if (discovered.size > 0) {
                yAxisFields = Array.from(discovered).sort();
                // compute a total for each row and add a 'Total' series
                for (const row of json) {
                    row._total = yAxisFields.reduce((sum, f) => sum + (Number(row[f]) || 0), 0);
                }
                yAxisFields.push('_total');
                isMultiLine = true;
                if (!this.config.yAxisNames) {
                    this.config.yAxisNames = yAxisFields.map(f => f === '_total' ? 'Total' : f);
                }
            }
        }

        Logger.debug("isMultiLine: ", isMultiLine, "isTimeSeries: ", isTimeSeries, "isCategoryAxis: ", isCategoryAxis);

        // sort category-axis data by the xAxis field (numeric order for derived day/hour)
        if (isCategoryAxis) {
            json.sort((a, b) => {
                const va = a[this.config.xAxis], vb = b[this.config.xAxis];
                return (va < vb ? -1 : va > vb ? 1 : 0);
            });
        }

        // sort the data by xAxis if time series
        if (isTimeSeries) {
            // filter invalid timestamps
            json = json.filter(item => {
                return item[this.config.xAxis] && !isNaN(parseInt(item[this.config.xAxis]));
            });
            json.sort((a, b) => {
                return parseInt(a[this.config.xAxis]) - parseInt(b[this.config.xAxis]);
            });
        }

        // NOTE: this shouldn't be needed anymore, all data should have rollups for this type of aggregation
        // if timeScope is set to month and the data is hourly, aggregate to daily totals
        if (json.length > 0 && isTimeSeries && !isCategoryAxis && (UserSession.get("timeScope") === 'month')) {
            const firstTimestamp = parseInt(json[0][this.config.xAxis]);
            const lastTimestamp = parseInt(json[json.length - 1][this.config.xAxis]);
            const msInDay = 24 * 60 * 60 * 1000;
            if ((lastTimestamp - firstTimestamp) / json.length < msInDay) {
                Logger.debug("aggregating hourly data to daily totals");
                let aggregatedData = {};
                json.forEach(item => {
                    const date = new Date(parseInt(item[this.config.xAxis]));
                    date.setUTCHours(0, 0, 0, 0);
                    const dayTimestamp = date.getTime();
                    if (!aggregatedData[dayTimestamp]) {
                        aggregatedData[dayTimestamp] = {};
                        aggregatedData[dayTimestamp][this.config.xAxis] = '' + dayTimestamp;
                        yAxisFields.forEach(field => {
                            aggregatedData[dayTimestamp][field] = 0;
                        });
                    }
                    // aggregate the yAxis fields based on the config (possible values: last, sum, avg)
                    if (this.config.aggregate === "last") {
                        yAxisFields.forEach(field => {
                            aggregatedData[dayTimestamp][field] = item[field];
                        });
                    } else {
                        // default to 'sum'
                        yAxisFields.forEach(field => {
                            aggregatedData[dayTimestamp][field] += Number(item[field]) || 0;
                        });
                    }
                });
                json = Object.values(aggregatedData);
                // sort the aggregated data by xAxis
                json.sort((a, b) => {
                    return parseInt(a[this.config.xAxis]) - parseInt(b[this.config.xAxis]);
                });
                Logger.debug("after aggregation json: ", json);
            }
        }

        // NOTE: this shouldn't be needed anymore, all data should have rollups for this type of aggregation
        // if timeScope is set to all and the data is hourly, aggregate to monthly totals
        if (json.length > 0 && isTimeSeries && !isCategoryAxis && (UserSession.get("timeScope") === 'all')) {
            const firstTimestamp = parseInt(json[0][this.config.xAxis]);
            const lastTimestamp = parseInt(json[json.length - 1][this.config.xAxis]);
            const msInDay = 24 * 60 * 60 * 1000;
            if ((lastTimestamp - firstTimestamp) / json.length < msInDay) {
                Logger.debug("aggregating hourly data to monthly totals");
                let aggregatedData = {};
                json.forEach(item => {
                    const date = new Date(parseInt(item[this.config.xAxis]));
                    date.setUTCDate(1);
                    date.setUTCHours(0, 0, 0, 0);
                    const monthTimestamp = date.getTime();
                    if (!aggregatedData[monthTimestamp]) {
                        aggregatedData[monthTimestamp] = {};
                        aggregatedData[monthTimestamp][this.config.xAxis] = '' + monthTimestamp;
                        yAxisFields.forEach(field => {
                            aggregatedData[monthTimestamp][field] = 0;
                        });
                    }
                    // aggregate the yAxis fields based on the config (possibly values: last, sum, avg)
                    if (this.config.aggregate === "last") {
                        yAxisFields.forEach(field => {
                            aggregatedData[monthTimestamp][field] = item[field];
                        });
                    } else {
                        // default to 'sum'
                        yAxisFields.forEach(field => {
                            aggregatedData[monthTimestamp][field] += Number(item[field]) || 0;
                        });
                    }
                });
                json = Object.values(aggregatedData);
                // sort the aggregated data by xAxis
                json.sort((a, b) => {
                    return parseInt(a[this.config.xAxis]) - parseInt(b[this.config.xAxis]);
                });
                Logger.debug("after aggregation json: ", json);
            }
        }

        // NOTE: this shouldn't be needed anymore, all data should have rollups for this type of aggregation
        // if timeScope is set to week and the data is hourly, aggregate to daily totals
        if (json.length > 0 && isTimeSeries && !isCategoryAxis && (UserSession.get("timeScope") === 'week')) {
            const firstTimestamp = parseInt(json[0][this.config.xAxis]);
            const lastTimestamp = parseInt(json[json.length - 1][this.config.xAxis]);
            const msInDay = 24 * 60 * 60 * 1000;
            if ((lastTimestamp - firstTimestamp) / json.length < msInDay) {
                Logger.debug("aggregating hourly data to daily totals");
                let aggregatedData = {};
                json.forEach(item => {
                    const date = new Date(parseInt(item[this.config.xAxis]));
                    date.setUTCHours(0, 0, 0, 0);
                    const dayTimestamp = date.getTime();
                    if (!aggregatedData[dayTimestamp]) {
                        aggregatedData[dayTimestamp] = {};
                        aggregatedData[dayTimestamp][this.config.xAxis] = '' + dayTimestamp;
                        yAxisFields.forEach(field => {
                            aggregatedData[dayTimestamp][field] = 0;
                        });
                    }
                    // aggregate the yAxis fields based on the config (possibly values: last, sum, avg)
                    if (this.config.aggregate === "last") {
                        yAxisFields.forEach(field => {
                            aggregatedData[dayTimestamp][field] = item[field];
                        });
                    } else {
                        // default to 'sum'
                        yAxisFields.forEach(field => {
                            aggregatedData[dayTimestamp][field] += Number(item[field]) || 0;
                        });
                    }
                });
                json = Object.values(aggregatedData);
                // sort the aggregated data by xAxis
                json.sort((a, b) => {
                    return parseInt(a[this.config.xAxis]) - parseInt(b[this.config.xAxis]);
                });
                Logger.debug("after aggregation json: ", json);
            }
        }

        // zero fill for specified fields, based on the time scope (day, week, month)
        if (Array.isArray(this.config.zeroFill)) {
            const timeScope = UserSession.get("timeScope") || 'month';
            // populate an array of timestamps for the selected time scope
            let timestamps = [];
            if (timeScope === 'day') {
                // set min to 24 hours before now
                xAxisOptions.max = new Date().setMinutes(0, 0, 0); // up to current hour
                xAxisOptions.min = new Date(xAxisOptions.max - (24 * 60 * 60 * 1000)).getTime(); // 24 hours before max
                // now get the timestamp for each day between min and max
                let date = new Date(xAxisOptions.min);
                while (date.getTime() <= xAxisOptions.max) {
                    timestamps.push(date.getTime());
                    date = new Date(date.getTime() + (60 * 60 * 1000)); // increment by 1 hour
                }
            } else if (timeScope === 'week') {
                xAxisOptions.max = new Date().setUTCHours(0, 0, 0, 0);
                xAxisOptions.min = new Date(xAxisOptions.max - (7 * 24 * 60 * 60 * 1000)).getTime(); // 7 days before max
                // now get the timestamp for each day between min and max
                let date = new Date(xAxisOptions.min);
                while (date.getTime() <= xAxisOptions.max) {
                    timestamps.push(date.getTime());
                    date = new Date(date.getTime() + (24 * 60 * 60 * 1000)); // increment by 1 day
                }
            } else if (timeScope === 'month') {
                xAxisOptions.max = new Date().setUTCHours(0, 0, 0, 0);
                // set min to 30 days prior to max
                xAxisOptions.min = new Date(new Date(xAxisOptions.max).setUTCDate(new Date(xAxisOptions.max).getUTCDate() - 30)).getTime();
                // now get the UTC timestamp for each day between min and max
                let date = new Date(xAxisOptions.min);
                while (date.getTime() <= xAxisOptions.max) {
                    timestamps.push(date.getTime());
                    date = new Date(date.getTime() + (24 * 60 * 60 * 1000)); // increment by 1 day
                }
            } else if (timeScope === 'quarter') {
                const quarterStart = Utils.getCurrentUTCQuarter();
                xAxisOptions.min = quarterStart;
                xAxisOptions.max = quarterStart;
                timestamps.push(quarterStart);
            } else {
                // all time, zero fill from first to last timestamp in the monthly data
                if (json.length > 0) {
                    const firstTimestamp = parseInt(json[0][this.config.xAxis]);
                    xAxisOptions.max = new Date(new Date().getFullYear(), new Date().getMonth(), 1); // first of current month
                    xAxisOptions.min = new Date(firstTimestamp);
                    // now get the UTC timestamp for each month between min and max
                    let date = new Date(xAxisOptions.min);
                    while (date.getTime() <= xAxisOptions.max) {
                        timestamps.push(date.getTime());
                        date = new Date(new Date(date).setUTCMonth(date.getUTCMonth() + 1)); // increment by 1 month
                    }
                }
            }

            Logger.debug("zero fill timestamps: ", timestamps);

            // zero fill for each timestamp
            for (let timestamp of timestamps) {
                let item = json.find(x => parseInt(x[this.config.xAxis]) === timestamp);
                if (!item) {
                    item = {};
                    item[this.config.xAxis] = '' + timestamp;
                    this.config.zeroFill.forEach(field => {
                        item[field] = 0;
                    });
                    json.push(item);
                } else {
                    this.config.zeroFill.forEach(field => {
                        if (item[field] === undefined || item[field] === null || item[field] === '') {
                            item[field] = 0;
                        }
                    });
                }
            }

            // sort the data by xAxis if time series
            if (isTimeSeries) {
                json.sort((a, b) => {
                    return parseInt(a[this.config.xAxis]) - parseInt(b[this.config.xAxis]);
                });
            }

            Logger.debug("after zero fill json: ", json);
        }

        this.exportableData = json;

        if (isMultiLine) {
            yAxisFields.forEach((field, idx) => {
                legendData.push(field);
                let fieldData;
                if (isTimeSeries) {
                    fieldData = json.map(x => [ new Date(parseInt(x[this.config.xAxis])), x[field] ]);
                } else if (isCategoryAxis) {
                    fieldData = json.map(x => x[field]);
                } else {
                    fieldData = json.map(x => [ x[this.config.xAxis], x[field] ]);
                }
                series.push({
                    name: this.config.yAxisNames?.[idx] ?? field,
                    type: this.config.chartType || 'line',
                    data: fieldData,
                    showSymbol: true,
                    smooth: 0.3,
                    //silent: true
                    //label: { show: false, position: 'top' },
                    //emphasis: { disabled: true }
                });
                Logger.debug("series for field ", field, ": ", fieldData);
            });
            // Set legend dynamically
            let legendOptions = {
                show: true,
                data: this.config.yAxisNames
            };
            /*
            // Optionally set xAxis label formatter if specified
            if (xAxisOptions.axisLabel && xAxisOptions.axisLabel.formatter === "dateMMDDYY" && isTimeSeries) {
                xAxisOptions.axisLabel.formatter = function (value) {
                    const d = new Date(value);
                    const mm = String(d.getMonth() + 1).padStart(2, '0');
                    const dd = String(d.getDate()).padStart(2, '0');
                    const yy = String(d.getFullYear()).slice(-2);
                    return `${mm}/${dd}/${yy}`;
                };
            }
            */
            if (isTimeSeries) {
                // set a locale-based formatter and use timeScope to set granularity
                // possible timeScopes: day, week, month, all
                let timeScope = UserSession.get("timeScope") || 'month';
                xAxisOptions.axisLabel = xAxisOptions.axisLabel || {};
                xAxisOptions.axisLabel.formatter = (value) => this.getFriendlyTimestamp(value, timeScope, true);
                // use the same formatter for the tooltip
                tooltipOptions.formatter = (params) => {  
                    let tooltipText = '';
                    if (params[0]?.axisValue) {
                        tooltipText = this.getFriendlyTimestamp(params[0]?.axisValue, timeScope, false);
                        tooltipText += '<br/>';
                    }
                    params.forEach(p => {
                        tooltipText += `${p.seriesName}: ${p.data[1]}<br/>`;
                    });
                    return tooltipText;
                };
            }

            // category axis: supply labels and set type
            if (isCategoryAxis) {
                const labels = this.config.xAxisLabels
                    ? json.map(x => this.config.xAxisLabels[String(x[this.config.xAxis])] ?? x[this.config.xAxis])
                    : json.map(x => x[this.config.xAxis]);
                xAxisOptions = Object.assign({ type: 'category', data: labels }, xAxisOptions);
            }

            this.chart.setOption({
                animation: false,
                xAxis: xAxisOptions,
                legend: legendOptions,
                series,
                tooltip: tooltipOptions
            });
        } else {
            let data;
            const resolvedLabels = this.resolveXAxisLabels();
            if (isCategoryAxis) {
                // determine the full set of expected category keys
                let allKeys;
                if (this.config.xAxisAllKeys) {
                    allKeys = this.config.xAxisAllKeys.map(String);
                } else if (resolvedLabels && typeof resolvedLabels === 'object') {
                    allKeys = Object.keys(resolvedLabels).sort((a, b) => Number(a) - Number(b));
                } else {
                    allKeys = null;
                }
                // zero-fill missing keys
                if (allKeys) {
                    const xField = this.config.xAxis;
                    const existing = new Set(json.map(x => String(x[xField])));
                    for (const key of allKeys) {
                        if (!existing.has(key)) {
                            const row = { [xField]: Number(key) };
                            (Array.isArray(this.config.yAxis) ? this.config.yAxis : [this.config.yAxis]).forEach(f => { row[f] = 0; });
                            json.push(row);
                        }
                    }
                    json.sort((a, b) => Number(a[xField]) - Number(b[xField]));
                }
                const labels = resolvedLabels
                    ? json.map(x => resolvedLabels[String(x[this.config.xAxis])] ?? x[this.config.xAxis])
                    : json.map(x => x[this.config.xAxis]);
                xAxisOptions = Object.assign({ type: 'category', data: labels }, xAxisOptions);
                data = json.map(x => x[this.config.yAxis]);
            } else if (isTimeSeries) {
                data = json.map(x => [ new Date(parseInt(x[this.config.xAxis])), x[this.config.yAxis] ]);
                if (xAxisOptions.axisLabel && xAxisOptions.axisLabel.formatter === "dateMMDDYY") {
                    xAxisOptions.axisLabel.formatter = function (value) {
                        const d = new Date(value);
                        const mm = String(d.getMonth() + 1).padStart(2, '0');
                        const dd = String(d.getDate()).padStart(2, '0');
                        const yy = String(d.getFullYear()).slice(-2);
                        return `${mm}/${dd}/${yy}`;
                    };
                }
            } else {
                data = json.map(x => [ x[this.config.xAxis], x[this.config.yAxis] ]);
            }
            let yAxisOptions = this.config.chartOptions?.yAxis || { type: 'value' };
            let gridOptions = this.config.chartOptions?.grid || undefined;
            if (this.config.yAxisFormatter === 'duration_hm') {
                yAxisOptions.axisLabel = { formatter: (v) => this.formatDuration(v) };
                gridOptions = gridOptions || { left: 100 };
                tooltipOptions.formatter = (params) => {
                    const p = Array.isArray(params) ? params[0] : params;
                    const label = isTimeSeries ? this.getFriendlyTimestamp(p.axisValue || p.data[0], UserSession.get('timeScope') || 'day', false) : (p.name || '');
                    return `${label}<br/>${this.formatDuration(p.data?.[1] ?? p.data ?? p.value)}`;
                };
            }
            const seriesType = this.config.chartType || 'bar';
            const seriesOpts = { type: seriesType, data };
            if (seriesType === 'bar') { seriesOpts.colorBy = 'data'; }
            const opts = { 
                xAxis: xAxisOptions,
                yAxis: yAxisOptions,
                series: [seriesOpts],
                tooltip: tooltipOptions
            };
            if (gridOptions) { opts.grid = gridOptions; }
            this.chart.setOption(opts);
        }
    }

    redraw() {
        const title = Utils.evaluate(this.config.title, [ 'state' ], [ UserSession.state ]);
        this.chart.setOption({ title: { text: title } });
        this.chart?.resize();
    }

    getExportData() {
        let timeScope = UserSession.get("timeScope") || 'month';
        let isTimeSeries = !!this.config.src?.timeSeries;
        const yAxisFields = Array.isArray(this.config.yAxis) ? this.config.yAxis : [this.config.yAxis];
        let cols = yAxisFields.map((col, idx) => ({ header: this.config.yAxisNames?.[idx] ?? col, key: col, width: 20 }));
        let rows = [];

        Logger.debug("get data: ", this.exportableData);

        if (isTimeSeries) {
            rows = this.exportableData.map(row => 
                cols.reduce((accum,col) => { accum[col.key] = row[col.key]; return accum; },
                    { [this.config.xAxis]: this.getFriendlyTimestamp(parseInt(row[this.config.xAxis]), timeScope, false) }));
            cols.unshift({ header: 'Time', key: this.config.xAxis, width: 20 });
        } else {
            rows = this.exportableData.map(row => 
                cols.reduce((accum,col) => { accum[col.key] = row[col.key]; return accum; },
                    { [this.config.xAxis]: row[this.config.xAxis] }));
            cols.unshift({ header: this.config.xAxis, key: this.config.xAxis, width: 20 });
        }
        return { name: this.config.exportTitle, cols, rows };
    }
}
customElements.define('hs-chart', HSChart);
